/**
 * Permissions UI Tree
 *
 * Defines the hierarchical structure and rendering order of permissions.
 * This tree determines how permissions are organized and displayed in the UI.
 *
 * All text content uses i18n keys from roles.json:
 * - Section/Tab names: permissionsTree.sections.{key}.name
 * - Section descriptions: permissionsTree.sections.{key}.description
 * - Tab names: permissionsTree.tabs.{key}
 * - Group names: permissionsTree.groups.{key}.name
 * - Group descriptions: permissionsTree.groups.{key}.description
 * - Permission names: permissions.{frontendId}.name
 * - Permission descriptions: permissions.{frontendId}.description
 */

import type { PermissionsUITree } from './permissionsUITree.types';

export const PERMISSIONS_UI_TREE: PermissionsUITree = {
  // ============================================
  // ORGANIZATION LEVEL
  // ============================================
  organization: {
    sections: [
      {
        i18nKey: 'organizationPortal',
        containers: [
          {
            type: 'group',
            i18nKey: 'orgModules',
            iconName: 'monitor',
            permissions: [
              { frontendId: 'org-gift-certificates', component: 'checkbox' },
              { frontendId: 'org-inventory', component: 'checkbox' },
              { frontendId: 'org-menu', component: 'checkbox' },
              { frontendId: 'org-personnel', component: 'checkbox' },
            ],
          },
        ],
      },
    ],
  },

  // ============================================
  // STORE LEVEL
  // ============================================
  store: {
    sections: [
      {
        i18nKey: 'storePermissions',
        permissions: [
          {
            frontendId: 'store-admin',
            component: 'switch',
          },
        ],
        tabs: [
          // ============================================
          // TAB: PORTAL ADMINISTRADOR
          // ============================================
          {
            i18nKey: 'portalAdmin',
            sections: [
              {
                i18nKey: 'storePortal',
                containers: [
                  {
                    type: 'group',
                    i18nKey: 'storeModules',
                    iconName: 'monitor',
                    permissions: [
                      { frontendId: 'store-portal-config', component: 'checkbox' },
                      { frontendId: 'store-portal-tickets', component: 'checkbox' },
                      { frontendId: 'store-portal-invoicing', component: 'checkbox' },
                      { frontendId: 'store-portal-menu', component: 'checkbox' },
                      { frontendId: 'store-portal-inventory', component: 'checkbox' },
                      { frontendId: 'store-portal-reports', component: 'checkbox' },
                      { frontendId: 'store-portal-subscription', component: 'checkbox' },
                    ],
                  },
                ],
              },
            ],
          },

          // ============================================
          // TAB: CONFIGURACIÓN DISPOSITIVOS
          // ============================================
          {
            i18nKey: 'devicesConfig',
            sections: [
              {
                i18nKey: 'devicesConfig',
                permissions: [
                  {
                    frontendId: 'store-devices-configure',
                    component: 'switch',
                  },
                ],
              },
            ],
          },

          // ============================================
          // TAB: OPERACIÓN
          // ============================================
          {
            i18nKey: 'operation',
            sections: [
              {
                i18nKey: 'operation',
                permissions: [
                  {
                    frontendId: 'store-pos-access',
                    component: 'switch',
                  },
                ],
                // When store-pos-access is OFF, all containers below are disabled
                gatekeeperPermissionId: 'store-pos-access',
                containers: [
                  // ============================================
                  // GESTIÓN DE OPERACIÓN (Módulos con switch)
                  // ============================================
                  {
                    type: 'module-with-switch',
                    parentSwitch: {
                      frontendId: 'store-pos-cash-module',
                      component: 'switch',
                    },
                    iconName: 'computer',
                    permissions: [
                      { frontendId: 'store-pos-cash-open', component: 'checkbox' },
                      { frontendId: 'store-pos-cash-deposit', component: 'checkbox' },
                      { frontendId: 'store-pos-cash-withdraw', component: 'checkbox' },
                      { frontendId: 'store-pos-cash-tips-report', component: 'checkbox' },
                    ],
                  },
                  {
                    type: 'module-with-switch',
                    parentSwitch: {
                      frontendId: 'store-pos-config-module',
                      component: 'switch',
                    },
                    iconName: 'settings',
                    permissions: [{ frontendId: 'store-pos-config-fingerprint', component: 'checkbox' }],
                  },
                  {
                    type: 'module-with-switch',
                    parentSwitch: {
                      frontendId: 'store-pos-tables-module',
                      component: 'switch',
                    },
                    iconName: 'utensils',
                    permissions: [
                      { frontendId: 'store-pos-tables-change', component: 'checkbox' },
                      { frontendId: 'store-pos-tables-change-waiter', component: 'checkbox' },
                      { frontendId: 'store-pos-tables-transfer', component: 'checkbox' },
                    ],
                  },
                  {
                    type: 'module-with-switch',
                    parentSwitch: {
                      frontendId: 'store-pos-orders-module',
                      component: 'switch',
                    },
                    iconName: 'concierge-bell',
                    permissions: [
                      { frontendId: 'store-pos-orders-force-close', component: 'checkbox' },
                      { frontendId: 'store-pos-orders-pause-resume', component: 'checkbox' },
                    ],
                  },

                  // ============================================
                  // GESTIÓN DE CUENTAS (Accordion sin switch)
                  // ============================================
                  {
                    type: 'group',
                    i18nKey: 'accountsManagement',
                    iconName: 'ticket',
                    permissions: [
                      { frontendId: 'store-pos-accounts-gift-cert', component: 'checkbox' },
                      { frontendId: 'store-pos-accounts-discounts', component: 'checkbox' },
                      { frontendId: 'store-pos-accounts-restricted-discounts', component: 'checkbox' },
                      { frontendId: 'store-pos-accounts-auto-logout', component: 'checkbox' },
                      { frontendId: 'store-pos-accounts-create', component: 'checkbox' },
                      { frontendId: 'store-pos-accounts-edit-after-print', component: 'checkbox' },
                      { frontendId: 'store-pos-accounts-reprint-order', component: 'checkbox' },
                      { frontendId: 'store-pos-accounts-reprint-receipt', component: 'checkbox' },
                      { frontendId: 'store-pos-accounts-view-others', component: 'checkbox' },
                    ],
                  },

                  // ============================================
                  // ACCIONES DE USUARIOS (Accordion sin switch)
                  // ============================================
                  {
                    type: 'group',
                    i18nKey: 'userActions',
                    iconName: 'users',
                    permissions: [{ frontendId: 'store-pos-users-logout-all', component: 'checkbox' }],
                  },

                  // ============================================
                  // GESTIÓN DE PAGOS Y CANCELACIONES (Accordion sin switch)
                  // ============================================
                  {
                    type: 'group',
                    i18nKey: 'paymentsManagement',
                    iconName: 'circle-dollar-sign',
                    permissions: [
                      { frontendId: 'store-pos-payments-update', component: 'checkbox' },
                      { frontendId: 'store-pos-payments-cancel-items', component: 'checkbox' },
                      { frontendId: 'store-pos-payments-cancel-service-charge', component: 'checkbox' },
                      { frontendId: 'store-pos-payments-cancel-account', component: 'checkbox' },
                      { frontendId: 'store-pos-payments-cancel-payment', component: 'checkbox' },
                      { frontendId: 'store-pos-payments-edit-closed', component: 'checkbox' },
                      { frontendId: 'store-pos-payments-register', component: 'checkbox' },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
};
